import { createContext, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import YoutubeHelper from 'src/common/ytHelper/YoutubeHelper'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { getDataPublic } from 'src/consumer/services/profileService'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'

export const DocSideContext = createContext(null)

export default function DocSideContextProvider({ children }) {
  const [showDocSide, setShowDocSide] = useState(true)
  const [flowId, setFlowId] = useState()
  const [contextId, setContextId] = useState()
  const [buttonTopOffset, setButtonTopOffset] = useState(0)
  const [productContexts, setProductContexts] = useState([])
  const [isInIframe, setIsInIframe] = useState(false)
  const [tokenValid, setTokenValid] = useState(() => {
    const token = localStorage.getItem('token')
    return !isTokenInvalidOrAbsent(token)
  })

  const { authToken } = useContext(ProfileContext)
  const location = useLocation()

  // Listen to storage events to immediately react to token changes (e.g., on logout)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('token')
      setTokenValid(!isTokenInvalidOrAbsent(token))
    }

    // Check token on mount and when authToken from context changes
    handleStorageChange()

    // Listen to storage events (triggered by logoutHandler)
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [authToken])

  async function getProductContexts() {
    const response = await getDataPublic('/api/health/product-contexts')
    if (response.status === 200) {
      setProductContexts(response.data)
    }
  }

  // Filter contexts to only include amhm-web-app items
  const filteredContexts = productContexts.filter(
    (context) => context.productAppId === 'amhm-web-app',
  )

  // Lazy load API call - only fetch when YoutubeHelper is actually going to be shown
  // This prevents blocking page rendering with an unnecessary API call
  useEffect(() => {
    // Only fetch product contexts when YoutubeHelper will actually be shown
    // This defers the API call until it's actually needed, preventing it from blocking page render
    const shouldShowYoutubeHelper = 
      showDocSide && 
      tokenValid && 
      !isTokenInvalidOrAbsent(authToken) && 
      !isTokenInvalidOrAbsent(localStorage.getItem('token')) && 
      !isInIframe

    if (shouldShowYoutubeHelper && productContexts.length === 0) {
      getProductContexts()
    }
  }, [showDocSide, tokenValid, authToken, isInIframe, productContexts.length])

  // Check if the app is running inside an iframe
  useEffect(() => {
    const checkIfInIframe = () => {
      try {
        // Check if we're in an iframe by comparing window.top with window.self
        const inIframe = window.top !== window.self
        setIsInIframe(inIframe)
      } catch (error) {
        // If we can't access window.top (due to cross-origin restrictions), 
        // we're likely in an iframe
        setIsInIframe(true)
      }
    }

    checkIfInIframe()
  }, [])

  // Update contextId when location or productContexts change
  useEffect(() => {
    // Function to find the closest matching context path
    function findMatchingContext(currentPath) {
      if (!filteredContexts.length) return null

      let bestMatch = null
      let bestMatchLength = 0

      for (const context of filteredContexts) {
        if (context.contextPaths) {
          for (const contextPath of context.contextPaths) {
            // Check if current path starts with the context path
            if (currentPath.startsWith(contextPath)) {
              // Prefer longer matches (more specific)
              if (contextPath.length > bestMatchLength) {
                bestMatch = context
                bestMatchLength = contextPath.length
              }
            }
          }
        }
      }

      return bestMatch
    }

    const matchingContext = findMatchingContext(location.pathname)
    if (matchingContext) {
      setContextId(matchingContext.id)
    } else {
      setContextId(null)
    }

    // Debug logging removed - uncomment for debugging if needed
    // console.log({ matchingContext, locationPath: location.pathname })
  }, [location.pathname, filteredContexts])

  return (
    <DocSideContext.Provider
      value={{
        showDocSide,
        setShowDocSide,
        flowId,
        setFlowId,
        buttonTopOffset,
        setContextId,
        setButtonTopOffset,
        contextId,
        productContexts: filteredContexts,
        isInIframe,
      }}
    >
      {children}
    </DocSideContext.Provider>
  )
}

/**
 * DocSideHelper - Renders YoutubeHelper widget after page content
 * This component is rendered separately after RenderRoutes to prevent blocking page rendering
 */
export function DocSideHelper() {
  const { showDocSide, contextId, flowId, buttonTopOffset, isInIframe } = useContext(DocSideContext)
  const { authToken } = useContext(ProfileContext)
  const tokenValid = !isTokenInvalidOrAbsent(localStorage.getItem('token'))

  // Kiosk + Display (OOH) screens are unattended public-facing surfaces — the
  // call/help widget has no place there.
  const isKioskDisplay =
    typeof window !== 'undefined' &&
    (window.location?.pathname?.startsWith('/kiosk/') ||
      window.location?.pathname?.startsWith('/display/'))

  const shouldShow =
    !isKioskDisplay &&
    showDocSide &&
    tokenValid &&
    !isTokenInvalidOrAbsent(authToken) &&
    !isTokenInvalidOrAbsent(localStorage.getItem('token')) &&
    !isInIframe

  if (!shouldShow) {
    return null
  }

  return (
    <YoutubeHelper
      fetchUrl="/api/secure/doc-side-content"
      flowId={flowId}
      contextId={contextId}
      buttonTopOffset={buttonTopOffset}
    />
  )
}
